/**
 * live/undo.ts — annullamento dell'ultima azione, in un solo posto.
 *
 * L'operatore (route token) e l'area riservata (route admin) annullano con le
 * stesse regole: duplicare la logica significherebbe far divergere i percorsi.
 * Qui c'è solo il calcolo dello stato ripristinato, a partire dall'ultimo evento
 * registrato e dallo stato corrente (lettura dal DB e locking restano nelle route).
 *
 * **Cosa si annulla.** L'ultima *azione*, non solo l'ultimo punto: impostare il
 * servizio, chiamare un timeout, cambiare stato, chiudere un set col rally point.
 * Perché sia possibile, ogni evento registrato porta con sé `previousState`, lo
 * snapshot dei campi che un'azione può cambiare. (È lo stesso contratto dello
 * storico di Dinamo, che salvava lo stato intero.)
 *
 * Gli eventi scritti **prima** di questo contratto non hanno lo snapshot: per
 * quelli resta la vecchia strada, che sa annullare solo i punti (`previousCurrentScore`,
 * `previousServiceTeam`, `previousSetState`).
 */
import type { MatchState } from './state'
import { liveEventLabel } from './event-labels'

/** Tipi di evento che assegnano un punto: gli unici annullabili senza snapshot. */
export const SCORING_EVENT_TYPES = new Set([
  'point', 'ace', 'attack', 'block', 'defense', 'reception', 'opponent_error',
])

export const UNDO_NOTHING = 'non c’è nessuna azione da annullare'

/**
 * Finestra in cui un undo è ammesso **anche** su una sessione chiusa.
 *
 * PERCHÉ esiste: l'undo di una partita conclusa serve a rimediare al tocco
 * sbagliato sul match point, non a riscrivere un referto. Senza limite, chi
 * possiede un codice operatore poteva cambiare il risultato finale in qualsiasi
 * momento (anche a distanza di giorni), perché la sessione `finished` è
 * modificabile solo da `UNDO_LAST_POINT`. Dieci minuti sono la finestra in cui
 * l'operatore è ancora in campo; oltre, la correzione passa dall'area riservata
 * con un evento tracciato.
 */
export const UNDO_WINDOW_MS = 10 * 60_000

/** `true` se lo stato chiude la sessione (nessuna azione è più ammessa). */
export function isClosedStatus(status: string | null | undefined): boolean {
  return status === 'finished' || status === 'cancelled'
}

/**
 * L'undo su una sessione chiusa è ancora ammesso?
 *
 * `closedAt` è l'istante di chiusura (`finished_at`, oppure `updated_at` per le
 * sessioni annullate, che non hanno `finished_at`). Restituisce `null` quando la
 * finestra è aperta, altrimenti il motivo del rifiuto — così operatore e area
 * riservata dicono la stessa cosa. Data assente = fail-closed: senza un istante
 * di chiusura certo non si può dimostrare che la finestra sia aperta.
 */
export function undoWindowError(
  status: string | null | undefined,
  closedAt: Date | string | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!isClosedStatus(status)) return null
  if (closedAt === null || closedAt === undefined) {
    return 'sessione chiusa: non è registrato l’istante di chiusura, l’annullamento non è ammesso'
  }
  const closedMs = closedAt instanceof Date ? closedAt.getTime() : new Date(closedAt).getTime()
  if (!Number.isFinite(closedMs)) {
    return 'sessione chiusa: istante di chiusura illeggibile, l’annullamento non è ammesso'
  }
  if (now - closedMs > UNDO_WINDOW_MS) {
    return 'sessione chiusa da più di 10 minuti: l’annullamento non è più ammesso'
  }
  return null
}

export interface LastLiveEvent {
  id: number
  type: string
  team: 'home' | 'away' | null
  payload: Record<string, unknown> | null
}

/**
 * Ultimo evento **annullabile** della partita in corso.
 *
 * PERCHÉ non basta «l'ultimo evento» (`ORDER BY id DESC LIMIT 1`). Annullare
 * scrive a sua volta un evento (`correction` con `reason: 'undo_last_action'`),
 * quindi il secondo undo trovava come «ultima azione» la correzione del primo e
 * ne ripristinava lo stato precedente — cioè **il punteggio di prima**: 3-0 →
 * undo → 2-0 → undo → 3-0. Un pendolo fra due stati, invece di camminare
 * all'indietro (3-0 → 2-0 → 1-0 → 0-0). È il difetto segnalato dal referente.
 *
 * Qui si escludono le correzioni d'annullamento e gli eventi che risultano già
 * annullati (`revertedEventId` scritto nella correzione): resta l'ultima azione
 * ancora **in vigore**, che è esattamente quella da annullare. Vale per i punti
 * come per i gesti tecnici: l'undo cammina all'indietro su tutto, un evento alla
 * volta, ed è l'operatore a decidere quanti passi fare.
 */
const UNDOABLE_EVENT_SQL = `
  SELECT e.id::int AS id, e.type, e.team, e.payload
    FROM live_match_events e
   WHERE e.session_key = $1
     AND e.game_id = $2::uuid
     AND coalesce(e.payload->>'reason', '') <> 'undo_last_action'
     AND NOT EXISTS (
           SELECT 1
             FROM live_match_events c
            WHERE c.session_key = e.session_key
              AND c.game_id = e.game_id
              AND c.payload->>'reason' = 'undo_last_action'
              AND c.payload->>'revertedEventId' = e.id::text
         )
   ORDER BY e.id DESC
   LIMIT 1`

/** Esecutore minimo: un client di transazione o il pool bastano (serve `query`). */
interface UndoQueryable {
  query: (sql: string, values?: unknown[]) => Promise<{ rows: unknown[] }>
}

/**
 * Legge l'ultimo evento annullabile. Vive qui (e non nelle due route che
 * annullano) perché operatore e area riservata devono annullare **la stessa
 * cosa**: duplicare la query è il modo in cui le due strade divergono.
 */
export async function selectUndoableEvent(
  db: UndoQueryable,
  sessionKey: string,
  gameId: string,
): Promise<LastLiveEvent | undefined> {
  const res = await db.query(UNDOABLE_EVENT_SQL, [sessionKey, gameId])
  return res.rows[0] as LastLiveEvent | undefined
}

/**
 * Le righe registrate prima che l'annullamento diventasse generale non hanno lo
 * snapshot dello stato precedente: per loro l'unica strada è la vecchia, che sa
 * annullare i punti. Il messaggio dice **quale** azione è, per non far credere che
 * il limite sia una regola (non lo è: vale per il servizio come per tutto il resto,
 * da qui in avanti) e come rimediare.
 */
export function undoLegacyMessage(last: LastLiveEvent | undefined, originalType?: string | null): string {
  const label = liveEventLabel(originalType ?? last?.type).toLowerCase()
  const remedy =
    originalType === 'SET_SERVICE' || originalType === 'set_service' || last?.type === 'set_service'
      ? 'imposta di nuovo il servizio col pulsante Srv della squadra che serve davvero'
      : 'correggi lo stato dall’area riservata'
  return `Questa riga («${label}») è stata registrata prima dell’aggiornamento che rende annullabile ogni azione: ` +
    `dello storico si annullano solo i punti. Per rimediare, ${remedy}.`
}

/**
 * Campi che un'azione può cambiare: è esattamente ciò che l'undo ripristina.
 * Volutamente esplicito — identità della sessione, squadre, loghi, `gameId` e
 * tema del pannello restano fuori, così annullare un punto non ricalca
 * all'indietro una scelta fatta nel frattempo dall'area riservata.
 *
 * `visible` e `commentary` sono **opzionali** di proposito: sono entrati nello
 * snapshot con `SET_VISIBLE` e `SET_COMMENTARY`, quindi gli eventi scritti prima
 * non li hanno. In quel caso l'undo non deve toccarli (niente default: un
 * `visible ?? true` rimetterebbe in vetrina una gara nascosta nel frattempo, e
 * una riga di commento inventata finirebbe in onda), mentre per gli eventi nuovi
 * un'azione editoriale annullata deve tornare esattamente com'era.
 */
export interface UndoSnapshot {
  currentScore: MatchState['currentScore']
  setsWon: MatchState['setsWon']
  currentSet: number
  setHistory: MatchState['setHistory']
  timeoutsUsed: MatchState['timeoutsUsed']
  serviceTeam: MatchState['serviceTeam']
  firstServer: MatchState['firstServer']
  status: MatchState['status']
  isTiebreak: boolean
  isMatchFinished: boolean
  winner: MatchState['winner']
  /** `null` quando la partita non era ancora iniziata. */
  startedAt: string | null
  /** Assente negli snapshot anteriori a `SET_VISIBLE` (vedi sopra). */
  visible?: boolean
  /** Assente negli snapshot anteriori a `SET_COMMENTARY` (vedi sopra). */
  commentary?: { visible: boolean; text: string }
  /** Ultimo punto in attesa di attribuzione: l'undo deve toglierlo, non lasciarlo. */
  lastPoint?: MatchState['lastPoint']
}

export function snapshotForUndo(state: MatchState): UndoSnapshot {
  return {
    currentScore: { ...state.currentScore },
    setsWon: { ...state.setsWon },
    currentSet: state.currentSet,
    setHistory: state.setHistory.map((s) => ({ ...s })),
    timeoutsUsed: { ...state.timeoutsUsed },
    serviceTeam: state.serviceTeam,
    firstServer: state.firstServer,
    status: state.status,
    isTiebreak: state.isTiebreak,
    isMatchFinished: state.isMatchFinished,
    winner: state.winner,
    startedAt: state.startedAt ?? null,
    visible: state.visible,
    // `undefined` non viene serializzato: gli eventi senza commentary restano
    // senza il campo, e `applySnapshot` li lascia stare.
    commentary: state.commentary ? { ...state.commentary } : undefined,
    lastPoint: state.lastPoint ?? null,
  }
}

/** Campi salvati dai punti registrati prima dello snapshot completo. */
interface LegacyPointPayload {
  previousCurrentScore?: Partial<{ home: number; away: number }>
  previousServiceTeam?: 'home' | 'away' | null
  previousSetState?: Partial<UndoSnapshot>
  originalType?: string
}

export type UndoResult =
  | { state: MatchState; revertedEventId: number; revertedType: string; revertedTeam: 'home' | 'away' | null }
  | { error: string }

export function restoreAfterUndo(
  previous: MatchState,
  last: LastLiveEvent | undefined,
  gameId: string,
): UndoResult {
  if (!last) return { error: UNDO_NOTHING }

  const payload = (last.payload ?? {}) as Record<string, unknown> & LegacyPointPayload
  const snapshot = payload.previousState as UndoSnapshot | undefined

  if (snapshot && typeof snapshot === 'object' && snapshot.currentScore) {
    return {
      state: applySnapshot(previous, snapshot, gameId),
      revertedEventId: last.id,
      revertedType: last.type,
      revertedTeam: last.team,
    }
  }

  // ── Eventi precedenti al contratto dello snapshot ─────────────────────────
  if (!last.team || !SCORING_EVENT_TYPES.has(last.type)) {
    return { error: undoLegacyMessage(last, payload.originalType) }
  }

  const savedScore = payload.previousCurrentScore
  const currentScore =
    typeof savedScore?.home === 'number' && typeof savedScore?.away === 'number'
      ? { home: savedScore.home, away: savedScore.away }
      : { ...previous.currentScore, [last.team]: Math.max(0, previous.currentScore[last.team] - 1) }

  const savedService = payload.previousServiceTeam
  const serviceTeam =
    savedService === 'home' || savedService === 'away' || savedService === null
      ? savedService
      : previous.serviceTeam

  const state: MatchState = { ...previous, currentScore, serviceTeam, gameId }

  // Il punto poteva aver chiuso un set: quei campi sono nello snapshot di set.
  const prevSet = payload.previousSetState
  if (prevSet && typeof prevSet === 'object') {
    if (prevSet.setsWon) state.setsWon = prevSet.setsWon as MatchState['setsWon']
    if (typeof prevSet.currentSet === 'number') state.currentSet = prevSet.currentSet
    if (Array.isArray(prevSet.setHistory)) state.setHistory = prevSet.setHistory as MatchState['setHistory']
    if (prevSet.timeoutsUsed) state.timeoutsUsed = prevSet.timeoutsUsed as MatchState['timeoutsUsed']
    if (prevSet.firstServer !== undefined) state.firstServer = prevSet.firstServer as MatchState['firstServer']
    if (prevSet.status) state.status = prevSet.status as MatchState['status']
    if (typeof prevSet.isTiebreak === 'boolean') state.isTiebreak = prevSet.isTiebreak
    if (typeof prevSet.isMatchFinished === 'boolean') state.isMatchFinished = prevSet.isMatchFinished
    if (prevSet.winner !== undefined) state.winner = prevSet.winner as MatchState['winner']
  }

  return { state, revertedEventId: last.id, revertedType: last.type, revertedTeam: last.team }
}

function applySnapshot(base: MatchState, snap: UndoSnapshot, gameId: string): MatchState {
  return {
    ...base,
    gameId,
    currentScore: snap.currentScore,
    setsWon: snap.setsWon,
    currentSet: snap.currentSet,
    setHistory: snap.setHistory,
    timeoutsUsed: snap.timeoutsUsed,
    serviceTeam: snap.serviceTeam,
    firstServer: snap.firstServer,
    status: snap.status,
    isTiebreak: snap.isTiebreak,
    isMatchFinished: snap.isMatchFinished,
    winner: snap.winner,
    startedAt: snap.startedAt ?? undefined,
    // Solo se lo snapshot li porta: gli eventi anteriori a `SET_VISIBLE` /
    // `SET_COMMENTARY` non devono rimettere in vetrina una gara nascosta né
    // inventare una riga di commento.
    ...(typeof snap.visible === 'boolean' ? { visible: snap.visible } : {}),
    ...(snap.commentary ? { commentary: { ...snap.commentary } } : {}),
    // Lo snapshot può non avere il campo (eventi anteriori): in quel caso
    // l'annullamento non deve lasciare aperta una scelta di chi ha segnato.
    lastPoint: snap.lastPoint ?? null,
  }
}
